// A minimal floating context menu — used by map.ts for the right-click
// add/remove-pipe menu, the ambiguous-pick list (multiple pipes under the
// cursor), and the ctrl-drag box-select add/remove menu. Plain DOM, no
// framework: one menu open at a time, closed on outside click/Escape/scroll.
export interface ContextMenuItem {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

let activeMenu: HTMLDivElement | null = null;
let outsideHandler: ((event: PointerEvent) => void) | null = null;
let keyHandler: ((event: KeyboardEvent) => void) | null = null;

// --- items every right-click menu carries (page feedback, 27 Oct 2026) ------
// A window capture listener notes each right-click before any other handler
// runs; a menu opened while that event is still being dispatched (map.ts,
// assetIssues.ts, panel.ts ... all open theirs synchronously in their own
// contextmenu listener) gets the registered extra items appended, before a
// trailing "Cancel". A menu opened from a left click (a dropdown) does not.

/** The right-click being dispatched: what was clicked, and where. */
export interface ContextMenuOrigin {
  target: Element | null;
  clientX: number;
  clientY: number;
}

type ExtraItems = (origin: ContextMenuOrigin) => ContextMenuItem[];
const extraProviders: ExtraItems[] = [];
let dispatching: ContextMenuOrigin | null = null;
let openedDuringDispatch = false;

/** pageFeedback.ts: items added to every right-click menu. */
export function registerContextMenuExtras(fn: ExtraItems): void {
  extraProviders.push(fn);
}

/** The right-click currently being dispatched (null outside one). */
export function currentContextMenuOrigin(): ContextMenuOrigin | null {
  return dispatching;
}

/** True when a handler already opened a menu for the right-click being dispatched. */
export function menuOpenedForCurrentEvent(): boolean {
  return openedDuringDispatch;
}

if (typeof window !== "undefined") {
  window.addEventListener(
    "contextmenu",
    (event) => {
      dispatching = { target: event.target instanceof Element ? event.target : null, clientX: event.clientX, clientY: event.clientY };
      openedDuringDispatch = false;
      // cleared after this event's dispatch, even when a handler stops propagation
      setTimeout(() => {
        dispatching = null;
        openedDuringDispatch = false;
      }, 0);
    },
    { capture: true },
  );
}

function withExtras(items: ContextMenuItem[]): ContextMenuItem[] {
  if (!dispatching || extraProviders.length === 0) return items;
  const extras = extraProviders.flatMap((fn) => fn(dispatching!));
  const fresh = extras.filter((x) => !items.some((i) => i.label === x.label));
  if (fresh.length === 0) return items;
  const last = items[items.length - 1];
  if (last && last.label === "Cancel") return [...items.slice(0, -1), ...fresh, last];
  return [...items, ...fresh];
}

export function closeContextMenu(): void {
  if (!activeMenu) return;
  activeMenu.remove();
  activeMenu = null;
  if (outsideHandler) document.removeEventListener("pointerdown", outsideHandler, true);
  if (keyHandler) document.removeEventListener("keydown", keyHandler);
  outsideHandler = null;
  keyHandler = null;
}

/** Opens a menu at (clientX, clientY) — viewport coordinates, e.g. straight
 * from a MouseEvent. Closes any menu already open first (only one at a
 * time). */
export function showContextMenu(clientX: number, clientY: number, items: ContextMenuItem[]): void {
  closeContextMenu();
  if (items.length === 0) return;
  items = withExtras(items);
  if (dispatching) openedDuringDispatch = true;

  const menu = document.createElement("div");
  menu.style.position = "fixed";
  menu.style.left = `${clientX}px`;
  menu.style.top = `${clientY}px`;
  menu.style.zIndex = "1000";
  menu.style.background = "#fff";
  menu.style.color = "#1a1a1a";
  menu.style.border = "1px solid #ccc";
  menu.style.borderRadius = "4px";
  menu.style.boxShadow = "0 2px 10px rgba(0,0,0,0.25)";
  menu.style.padding = "0.25rem";
  menu.style.minWidth = "180px";
  menu.style.maxHeight = "60vh";
  menu.style.overflowY = "auto";
  menu.style.fontSize = "0.85rem";

  for (const item of items) {
    const btn = document.createElement("button");
    btn.textContent = item.label;
    btn.disabled = !!item.disabled;
    btn.style.display = "block";
    btn.style.width = "100%";
    btn.style.textAlign = "left";
    btn.style.padding = "0.4rem 0.6rem";
    btn.style.border = "none";
    btn.style.background = "transparent";
    btn.style.cursor = item.disabled ? "default" : "pointer";
    btn.style.color = item.disabled ? "#999" : "inherit";
    if (!item.disabled) {
      btn.addEventListener("mouseenter", () => (btn.style.background = "#f0f0f0"));
      btn.addEventListener("mouseleave", () => (btn.style.background = "transparent"));
      btn.addEventListener("click", () => {
        closeContextMenu();
        item.onClick();
      });
    }
    menu.append(btn);
  }

  document.body.append(menu);

  // Keep the menu on-screen if it would overflow the viewport edge.
  const rect = menu.getBoundingClientRect();
  if (rect.right > window.innerWidth) menu.style.left = `${Math.max(0, window.innerWidth - rect.width - 8)}px`;
  if (rect.bottom > window.innerHeight) menu.style.top = `${Math.max(0, window.innerHeight - rect.height - 8)}px`;

  activeMenu = menu;
  // Deferred so the click/contextmenu event that opened this menu doesn't
  // immediately close it via the outside-click listener.
  setTimeout(() => {
    outsideHandler = (event: PointerEvent) => {
      if (activeMenu && !activeMenu.contains(event.target as Node)) closeContextMenu();
    };
    keyHandler = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeContextMenu();
    };
    document.addEventListener("pointerdown", outsideHandler, true);
    document.addEventListener("keydown", keyHandler);
  }, 0);
}
